// B-138 — the closings behind a figure of the Dashboard: a KPI card, a day, a city, a zone, an office.
// The list says how they stand (the worst and the largest difference first), and a closing opens in
// the same detail the Panel opens — documents, alerts, attend, correct — so a figure leads to the
// work it stands for.
//
// The rows are the page's own when it has them (`filas`, already narrowed to the period and the
// map's offices). A committee day may fall outside the rows the page holds ("Todo" reads the newest
// 500), so a `dia` is asked of the server for that one date, as the Calendario asks for a day.
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Spinner, cn } from "@mantis-sas/ui";

import { filterOffices } from "../../../ApiCalls/mantisBusiness";
import { fechaDMY, moneda } from "../../../utils/cierrePanel";
import { CierreDetailDialog } from "./CierreDetailDialog";
import { EstadoBadge } from "./EstadoBadge";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const POR_PAGINA = 12;
const GRAVEDAD = { bloqueado: 0, pendiente: 1, cuadre: 2 };

/** The worst first: blocked, then for review, then reconciled; within each, the largest difference. */
export const ordenarCierres = (filas) => [...filas].sort((a, b) => (GRAVEDAD[a.estado] ?? 3) - (GRAVEDAD[b.estado] ?? 3)
  || Math.abs(b.diferencia) - Math.abs(a.diferencia)
  || String(b.fecha).localeCompare(String(a.fecha)));

/** The strip over the list: how many, how many reconcile, how many need someone, how much is off. */
export function resumenDeCierres(filas) {
  const cuadre = filas.filter((f) => f.estado === "cuadre").length;
  return {
    total: filas.length,
    cuadre,
    enRiesgo: filas.length - cuadre,
    discrepancia: filas.reduce((s, f) => s + (Number(f.diferencia) || 0), 0),
  };
}

const Dato = ({ etiqueta, valor, tono }) => (
  <div className="min-w-0 rounded-[10px] border bg-card px-3 py-2">
    <p className="truncate text-[11px] text-brinks-muted-foreground">{etiqueta}</p>
    <p className={cn("mt-0.5 font-lora text-[18px] font-semibold tabular-nums text-brinks-foreground", tono)}>{valor}</p>
  </div>
);

/**
 * @param {object} props
 * @param {?object} props.consulta  what was clicked, or null for closed:
 *        `{ titulo, subtitulo, filtro?(fila), dia?: 'YYYY-MM-DD', acciones?: [{ etiqueta, onClick }] }`
 * @param {object[]} props.filas    the page's rows (period and map already applied)
 * @param {Function} props.aFila    a panel row -> the page's row, for a day asked of the server
 * @param {?Set<string>} props.codigos  the map's offices, for a day asked of the server
 */
export function CierresDeLaCifra({ consulta, onClose, filas, aFila, codigos, token, mantisBusinessId, clientCode }) {
  const [delDia, setDelDia] = useState(null);
  const [error, setError] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [detalle, setDetalle] = useState(null);
  const dia = consulta?.dia || null;

  useEffect(() => {
    setPagina(1);
    setDelDia(null);
    setError(null);
    if (!dia) return undefined;
    let activo = true;
    filterOffices(token, { startDate: dia, endDate: dia }, 500, mantisBusinessId, "brinks", clientCode)
      .then((r) => {
        if (!activo) return;
        if (!r?.success) throw new Error("no");
        setDelDia((r.data || []).map(aFila).filter((f) => !codigos || codigos.has(f.codigo)));
      })
      .catch(() => {
        if (activo) setError("No se pudieron leer los cierres de ese día.");
      });
    return () => {
      activo = false;
    };
    // `aFila` and `codigos` belong to the page that asked; the day is the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consulta, token, mantisBusinessId, clientCode]);

  const lista = useMemo(() => {
    if (!consulta) return [];
    const base = dia ? delDia || [] : filas;
    return ordenarCierres(consulta.filtro ? base.filter(consulta.filtro) : base);
  }, [consulta, dia, delDia, filas]);
  const resumen = useMemo(() => resumenDeCierres(lista), [lista]);
  const paginas = Math.max(1, Math.ceil(lista.length / POR_PAGINA));
  const visibles = lista.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const cargando = Boolean(dia) && delDia === null && !error;

  return (
    <>
      <Dialog open={Boolean(consulta)} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="brinks-scope font-redhat flex max-h-[90vh] max-w-[880px] flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b p-4 pr-12 text-left">
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">{consulta?.titulo || "Cierres"}</DialogTitle>
              <BotonGuia guiaId="dialogo-cierres-de-la-cifra" titulo="Cierres de la cifra" texto="Los cierres que cuenta la cifra que tocaste, de los más graves a los que cuadran." nombre="Cierres de la cifra" />
            </div>
            <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
              {consulta?.subtitulo || "Los cierres detrás de esta cifra."} Toca un cierre para abrirlo.
            </DialogDescription>
            {consulta?.acciones?.length ? (
              <div {...guia("Ver solo esto", "Filtra la página de Reportes por esta oficina, ciudad o día.")} className="flex flex-wrap gap-2 pt-2">
                {consulta.acciones.map((a) => (
                  <Button
                    key={a.etiqueta}
                    size="sm"
                    variant="outline"
                    className="h-8 text-[12px]"
                    onClick={() => {
                      a.onClick();
                      onClose();
                    }}
                  >
                    {a.etiqueta}
                  </Button>
                ))}
              </div>
            ) : null}
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {cargando ? (
              <div className="grid place-items-center py-12"><Spinner /></div>
            ) : error ? (
              <p role="alert" className="py-10 text-center text-[12.5px] text-brinks-danger">{error}</p>
            ) : !lista.length ? (
              <p className="py-10 text-center text-[12.5px] text-brinks-muted-foreground">No hay cierres para esta cifra.</p>
            ) : (
              <>
                <div {...guia("Resumen", "Cuántos cierres hay, cuántos cuadran, cuántos están por atender y la discrepancia que suman.")} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Dato etiqueta="Cierres" valor={resumen.total} />
                  <Dato etiqueta="Cuadran" valor={resumen.cuadre} tono="text-brinks-success" />
                  <Dato etiqueta="Por atender" valor={resumen.enRiesgo} tono={resumen.enRiesgo ? "text-brinks-warning" : undefined} />
                  <Dato etiqueta="Discrepancia" valor={moneda(resumen.discrepancia)} tono={resumen.discrepancia ? "text-brinks-danger" : undefined} />
                </div>
                <div {...guia("Cierres", "Cada cierre con su fecha, oficina, estado y diferencia. Toca uno para abrirlo.")} className="mt-3 overflow-x-auto rounded-[10px] border">
                  <table className="w-full min-w-[620px] text-[12.5px]">
                    <thead className="bg-brinks-surface">
                      <tr className="text-left text-[10.5px] uppercase tracking-wide text-brinks-muted-foreground">
                        <th className="px-3 py-2 font-semibold">Fecha</th>
                        <th className="px-3 py-2 font-semibold">Oficina</th>
                        <th className="px-3 py-2 font-semibold">Región</th>
                        <th className="px-3 py-2 font-semibold">Estado</th>
                        <th className="px-3 py-2 text-right font-semibold">Diferencia</th>
                        <th className="w-8 px-2 py-2" aria-hidden />
                      </tr>
                    </thead>
                    <tbody>
                      {visibles.map((f) => (
                        <tr
                          key={f.id}
                          tabIndex={f.closingId ? 0 : -1}
                          onClick={() => f.closingId && setDetalle(f)}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && f.closingId) {
                              e.preventDefault();
                              setDetalle(f);
                            }
                          }}
                          aria-label={f.closingId ? `Abrir el cierre de ${f.oficina} del ${fechaDMY(f.fecha)}` : undefined}
                          className={cn(
                            "border-t text-brinks-foreground",
                            f.closingId ? "cursor-pointer hover:bg-brinks-surface-hover focus:bg-brinks-surface-hover focus:outline-none" : "opacity-70"
                          )}
                        >
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fechaDMY(f.fecha)}</td>
                          <td className="px-3 py-2">
                            <span className="font-medium">{f.oficina}</span>
                            <span className="ml-1.5 text-[10.5px] text-brinks-muted-foreground">{f.codigo}</span>
                          </td>
                          <td className="px-3 py-2 text-brinks-muted-foreground">{f.region}</td>
                          <td className="px-3 py-2"><EstadoBadge estado={f.estado} className="px-2 py-0.5 text-[11px]" /></td>
                          <td className={cn("px-3 py-2 text-right font-semibold tabular-nums", f.diferencia ? "text-brinks-danger" : "text-brinks-muted-foreground")}>
                            {moneda(f.diferencia)}
                          </td>
                          <td className="px-2 py-2 text-brinks-muted-foreground">{f.closingId ? <ExternalLink className="h-3.5 w-3.5" /> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {paginas > 1 ? (
                  <div className="mt-2 flex items-center justify-between text-[11.5px] text-brinks-muted-foreground">
                    <span className="tabular-nums">
                      {(pagina - 1) * POR_PAGINA + 1}–{Math.min(lista.length, pagina * POR_PAGINA)} de {lista.length}
                    </span>
                    <span className="flex items-center gap-1">
                      <button type="button" aria-label="Página anterior" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </button>
                      <span className="min-w-[48px] text-center tabular-nums">{pagina} / {paginas}</span>
                      <button type="button" aria-label="Página siguiente" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)} className="grid h-7 w-7 place-items-center rounded-[7px] border bg-card disabled:opacity-40">
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CierreDetailDialog
        closingId={detalle?.closingId || null}
        panelRow={detalle?.raw}
        token={token}
        mantisBusinessId={mantisBusinessId}
        onOpenChange={(open) => !open && setDetalle(null)}
      />
    </>
  );
}

export default CierresDeLaCifra;
